import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  TrashIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import { SectionEditor } from './SectionEditor';
import { FormPreview } from './FormPreview';
import { Button } from '../ui/Button';
import type { FormStructure, FormSection, FormQuestion, QuestionType } from '@affogato/shared';

interface FormBuilderProps {
  structure: FormStructure;
  onChange: (structure: FormStructure) => void;
  readOnly?: boolean;
}

export function FormBuilder({ structure, onChange, readOnly = false }: FormBuilderProps) {
  const { t } = useTranslation('diagnostics');
  const [showPreview, setShowPreview] = useState(false);

  const addSection = () => {
    const newSection: FormSection = {
      id: crypto.randomUUID(),
      title: t('builder.sectionTitle'),
      description: '',
      order: structure.sections.length,
      questions: [],
    };
    onChange({
      ...structure,
      sections: [...structure.sections, newSection],
    });
  };

  const updateSection = (sectionId: string, updates: Partial<FormSection>) => {
    onChange({
      ...structure,
      sections: structure.sections.map((s) =>
        s.id === sectionId ? { ...s, ...updates } : s
      ),
    });
  };

  const deleteSection = (sectionId: string) => {
    onChange({
      ...structure,
      sections: structure.sections
        .filter((s) => s.id !== sectionId)
        .map((s, idx) => ({ ...s, order: idx })),
    });
  };

  const moveSection = (sectionId: string, direction: 'up' | 'down') => {
    const idx = structure.sections.findIndex((s) => s.id === sectionId);
    if (idx === -1) return;

    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= structure.sections.length) return;

    const newSections = [...structure.sections];
    [newSections[idx], newSections[newIdx]] = [newSections[newIdx], newSections[idx]];

    onChange({
      ...structure,
      sections: newSections.map((s, i) => ({ ...s, order: i })),
    });
  };

  const addQuestion = (sectionId: string) => {
    const section = structure.sections.find((s) => s.id === sectionId);
    if (!section) return;

    const newQuestion: FormQuestion = {
      id: crypto.randomUUID(),
      text: t('builder.questionText'),
      description: '',
      type: 'scale' as QuestionType,
      required: true,
      order: section.questions.length,
      scaleConfig: {
        min: 1,
        max: 5,
        minLabel: '',
        maxLabel: '',
      },
    };

    updateSection(sectionId, {
      questions: [...section.questions, newQuestion],
    });
  };

  const updateQuestion = (sectionId: string, questionId: string, updates: Partial<FormQuestion>) => {
    const section = structure.sections.find((s) => s.id === sectionId);
    if (!section) return;

    updateSection(sectionId, {
      questions: section.questions.map((q) =>
        q.id === questionId ? { ...q, ...updates } : q
      ),
    });
  };

  const deleteQuestion = (sectionId: string, questionId: string) => {
    const section = structure.sections.find((s) => s.id === sectionId);
    if (!section) return;

    updateSection(sectionId, {
      questions: section.questions
        .filter((q) => q.id !== questionId)
        .map((q, idx) => ({ ...q, order: idx })),
    });
  };

  const moveQuestion = (sectionId: string, questionId: string, direction: 'up' | 'down') => {
    const section = structure.sections.find((s) => s.id === sectionId);
    if (!section) return;

    const idx = section.questions.findIndex((q) => q.id === questionId);
    if (idx === -1) return;

    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= section.questions.length) return;

    const newQuestions = [...section.questions];
    [newQuestions[idx], newQuestions[newIdx]] = [newQuestions[newIdx], newQuestions[idx]];

    updateSection(sectionId, {
      questions: newQuestions.map((q, i) => ({ ...q, order: i })),
    });
  };

  if (showPreview) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            {t('builder.preview')}
          </h2>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            {t('builder.title')}
          </Button>
        </div>
        <FormPreview structure={structure} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white">
          {t('builder.title')}
        </h2>
        <Button variant="secondary" onClick={() => setShowPreview(true)}>
          <EyeIcon className="w-4 h-4" />
          {t('builder.preview')}
        </Button>
      </div>

      {/* Sections */}
      <div className="space-y-4">
        {structure.sections.map((section, sectionIndex) => (
          <div
            key={section.id}
            className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden"
          >
            {/* Section Header */}
            <div className="flex items-center gap-2 p-4 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-200 dark:border-slate-700">
              <div className="flex-1">
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => updateSection(section.id, { title: e.target.value })}
                  disabled={readOnly}
                  placeholder={t('builder.sectionTitle')}
                  className="w-full bg-transparent border-0 p-0 text-lg font-medium text-gray-900 dark:text-white focus:ring-0 placeholder-gray-400 dark:placeholder-slate-500"
                />
                <input
                  type="text"
                  value={section.description || ''}
                  onChange={(e) => updateSection(section.id, { description: e.target.value })}
                  disabled={readOnly}
                  placeholder={t('builder.sectionDescription')}
                  className="w-full bg-transparent border-0 p-0 text-sm text-gray-500 dark:text-slate-400 focus:ring-0 placeholder-gray-400 dark:placeholder-slate-500 mt-1"
                />
              </div>
              {!readOnly && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveSection(section.id, 'up')}
                    disabled={sectionIndex === 0}
                    className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-30 transition-colors"
                  >
                    <ChevronUpIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                  </button>
                  <button
                    onClick={() => moveSection(section.id, 'down')}
                    disabled={sectionIndex === structure.sections.length - 1}
                    className="p-1.5 rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-30 transition-colors"
                  >
                    <ChevronDownIcon className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                  </button>
                  <button
                    onClick={() => deleteSection(section.id)}
                    className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                  >
                    <TrashIcon className="w-4 h-4 text-red-500 dark:text-red-400" />
                  </button>
                </div>
              )}
            </div>

            {/* Questions */}
            <div className="p-4 space-y-3">
              {section.questions.map((question, questionIndex) => (
                <SectionEditor
                  key={question.id}
                  question={question}
                  questionIndex={questionIndex}
                  totalQuestions={section.questions.length}
                  readOnly={readOnly}
                  onUpdate={(updates) => updateQuestion(section.id, question.id, updates)}
                  onDelete={() => deleteQuestion(section.id, question.id)}
                  onMove={(direction) => moveQuestion(section.id, question.id, direction)}
                />
              ))}

              {!readOnly && (
                <button
                  onClick={() => addQuestion(section.id)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-200 dark:border-slate-700 rounded-lg text-gray-500 dark:text-slate-400 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  <PlusIcon className="w-5 h-5" />
                  {t('builder.addQuestion')}
                </button>
              )}
            </div>
          </div>
        ))}

        {!readOnly && (
          <button
            onClick={addSection}
            className="w-full flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-xl text-gray-500 dark:text-slate-400 hover:border-primary-500 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <PlusIcon className="w-5 h-5" />
            {t('builder.addSection')}
          </button>
        )}
      </div>
    </div>
  );
}
